import { httpClient } from '@/core/api/httpClient';
import { API_V1 } from '@/core/api/endpoints';
import { env } from '@/core/config/env';
import type { PagedResult } from '@/shared/types/pagedResult';
import type {
  CreatedResponse,
  CreateTemplatePayload,
  FormDetail,
  TemplateDetail,
  TemplateSummary,
  TemplatesFilters,
  UpdateTemplatePayload,
} from '../types';
import { parseFieldDefinitions } from '../adapters/field-type.adapter';
import { toTemplateSummary } from './templates.mock';
import {
  defaultSharingFor,
  formsMockState,
  formsSharingMockState,
  templatesMockState,
} from './forms-mock-state';

const USE_MOCK = env.VITE_USE_MOCK === 'true';

/** Wire shape of `FormTemplateDetailDto` — `fieldDefinitions` travels as a JSON string, not a structured array. */
interface TemplateDetailWire {
  id: string;
  title: string;
  description: string | null;
  emailRequired: boolean;
  fieldDefinitions: string;
  createdAt: string;
  updatedAt: string;
}

function fromWireDetail(wire: TemplateDetailWire): TemplateDetail {
  return {
    id: wire.id,
    title: wire.title,
    description: wire.description,
    emailRequired: wire.emailRequired,
    fields: parseFieldDefinitions(wire.fieldDefinitions),
    createdAt: wire.createdAt,
    updatedAt: wire.updatedAt,
  };
}

export const templatesService = {
  getList: async (
    filters: TemplatesFilters,
    page: number,
    pageSize: number,
    signal?: AbortSignal,
  ) => {
    if (USE_MOCK) {
      const filtered = templatesMockState.filter((tpl) =>
        filters.searchTerm
          ? tpl.title.toLowerCase().includes(filters.searchTerm.toLowerCase())
          : true,
      );
      const start = (page - 1) * pageSize;
      const items = filtered.slice(start, start + pageSize).map(toTemplateSummary);
      const totalCount = filtered.length;
      const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
      return Promise.resolve({
        data: {
          items,
          page,
          pageSize,
          totalCount,
          totalPages,
          hasPreviousPage: page > 1,
          hasNextPage: page < totalPages,
          firstItemOnPage: totalCount === 0 ? 0 : start + 1,
          lastItemOnPage: Math.min(start + pageSize, totalCount),
        } satisfies PagedResult<TemplateSummary>,
      });
    }
    return httpClient.get<PagedResult<TemplateSummary>>(`${API_V1}/templates`, {
      params: { Page: page, PageSize: pageSize, SearchTerm: filters.searchTerm || undefined },
      signal,
    });
  },

  getById: async (templateId: string, signal?: AbortSignal) => {
    if (USE_MOCK) {
      const template = templatesMockState.find((t) => t.id === templateId);
      if (!template) return Promise.reject(new Error('FormTemplate.NotFound'));
      return Promise.resolve({ data: template });
    }
    const res = await httpClient.get<TemplateDetailWire>(`${API_V1}/templates/${templateId}`, {
      signal,
    });
    return { data: fromWireDetail(res.data) };
  },

  create: async (data: CreateTemplatePayload) => {
    if (USE_MOCK) {
      const newTemplate: TemplateDetail = {
        id: String(Date.now()),
        title: data.title,
        description: data.description ?? null,
        emailRequired: data.emailRequired,
        fields: data.fields.map((f) => ({
          ...f,
          id: null,
          options: f.options ?? null,
          validation: f.validation ?? null,
        })),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      templatesMockState.unshift(newTemplate);
      return Promise.resolve({ data: { id: newTemplate.id } satisfies CreatedResponse });
    }
    return httpClient.post<CreateTemplatePayload, CreatedResponse>(`${API_V1}/templates`, data);
  },

  update: async (templateId: string, data: UpdateTemplatePayload) => {
    if (USE_MOCK) {
      const idx = templatesMockState.findIndex((t) => t.id === templateId);
      if (idx === -1) return Promise.reject(new Error('FormTemplate.NotFound'));
      templatesMockState[idx] = {
        ...templatesMockState[idx],
        title: data.title,
        description: data.description ?? null,
        emailRequired: data.emailRequired,
        fields: data.fields.map((f) => ({
          ...f,
          id: null,
          options: f.options ?? null,
          validation: f.validation ?? null,
        })),
        updatedAt: new Date().toISOString(),
      };
      return Promise.resolve({ data: undefined as void });
    }
    return httpClient.put<UpdateTemplatePayload, void>(`${API_V1}/templates/${templateId}`, data);
  },

  delete: async (templateId: string) => {
    if (USE_MOCK) {
      const idx = templatesMockState.findIndex((t) => t.id === templateId);
      if (idx === -1) return Promise.reject(new Error('FormTemplate.NotFound'));
      templatesMockState.splice(idx, 1);
      return Promise.resolve({ data: undefined as void });
    }
    return httpClient.delete<void>(`${API_V1}/templates/${templateId}`);
  },

  /** Creates a copy of a template. Backend auto-prefixes with "Copia de " if no title is given. */
  duplicate: async (templateId: string, title?: string) => {
    if (USE_MOCK) {
      const original = templatesMockState.find((t) => t.id === templateId);
      if (!original) return Promise.reject(new Error('FormTemplate.NotFound'));
      const newTemplate: TemplateDetail = {
        ...original,
        id: String(Date.now()),
        title: title || `Copia de ${original.title}`,
        fields: original.fields.map((f) => ({ ...f })),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      templatesMockState.unshift(newTemplate);
      return Promise.resolve({ data: { id: newTemplate.id } satisfies CreatedResponse });
    }
    return httpClient.post<{ title?: string }, CreatedResponse>(
      `${API_V1}/templates/${templateId}/duplicate`,
      {
        title,
      },
    );
  },

  /** Creates a new Form in Draft/Private status from the template's fields. */
  instantiate: async (templateId: string) => {
    if (USE_MOCK) {
      const template = templatesMockState.find((t) => t.id === templateId);
      if (!template) return Promise.reject(new Error('FormTemplate.NotFound'));
      const newForm: FormDetail = {
        id: String(Date.now()),
        title: template.title,
        description: template.description,
        projectId: null,
        status: 'Draft',
        audienceType: 'Private',
        expiresAt: null,
        emailRequired: template.emailRequired,
        currentVersion: 0,
        fields: template.fields.map((f) => ({ ...f, id: null })),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      formsMockState.unshift(newForm);
      formsSharingMockState[newForm.id] = defaultSharingFor(newForm);
      return Promise.resolve({ data: { id: newForm.id } satisfies CreatedResponse });
    }
    return httpClient.post<undefined, CreatedResponse>(
      `${API_V1}/templates/${templateId}/instantiate`,
      undefined,
    );
  },
};
